<template>
  <div :class="wrapperClass">
    <div class="member-story__container">
      <div class="member-story__media">
        <cdr-img
          :src="image.src"
          :alt="image.alt"
          loading="lazy"
          class="member-story__img"
        />
      </div>
      <div class="member-story__content">
        <blockquote
          v-if="quote"
          class="member-story__quote"
          v-html="quote"
        />
        <figcaption
          v-if="author"
          class="member-story__author"
          v-html="author"
        />
        <div
          v-if="ctas && ctas.length"
          :class="[
            'member-story__cta-wrapper',
            ctas.length > 1 && 'member-story__cta-wrapper--multiple',
          ]"
        >
          <cdr-button
            v-for="(cta, index) in ctas"
            :key="index"
            :href="cta.target"
            :modifier="theme === 'dark' ? 'secondary' : 'primary'"
            tag="a"
            class="member-story__cta"
            v-html="cta.text"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CdrImg, CdrButton } from '@rei/cedar';

export default {
  name: 'MemberStory',
  components: {
    CdrImg,
    CdrButton,
  },
  props: {
    quote: {
      type: String,
      required: false,
      default: '',
    },
    author: {
      type: String,
      required: false,
      default: '',
    },
    ctas: {
      type: Array,
      required: false,
      default: () => [],
    },
    image: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    theme: {
      type: String,
      required: false,
      default: 'light',
    },
  },
  computed: {
    wrapperClass() {
      return [
        'member-story',
        this.theme && `member-story--${this.theme}`,
      ];
    },
  },
};
</script>

<style lang="scss">
@import '../../../../style/common/paths.scss';

.member-story {
  position: relative;
  padding: 0 $cdr-space-one-x;
  color: $cdr-color-text-brand;
  margin: 12.8rem 0 !important;

  &#{&}--dark {
    color: $cdr-color-text-inverse;
    background-color: $cdr-color-background-brand-spruce;

    @include container-rips($topRipXs, $bottomGreenRipXs);
    @include cdr-sm-mq-up {
      margin: 128px 0 162px !important;
      @include container-rips($topGreenRip, $bottomGreenRip);
    }
  }

  @include cdr-sm-mq-up {
    &__media,
    &__content {
      flex: 50%;
    }
  }

  &__container {
    @include cdr-container;
    border: 1px solid transparent;

    @include cdr-sm-mq-up {
      display: flex;
      flex-wrap: nowrap;
    }
  }

  &__media {
    position: relative;
    height: max-content;
    margin-top: calc($cdr-space-one-x * -1);
  }

  &__img {
    max-width: 100%;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: $cdr-space-one-x 0;

    @include cdr-sm-mq-up {
      padding: 3.6rem 0 3.6rem 2.4rem;
    }

    @include cdr-md-mq-up {
      padding: 3.2rem 0 3.2rem 6.4rem;
    }
  }

  &__author {
    @include cdr-text-utility-sans-strong-300;
    display: block;
  }

  &__quote {
    @include cdr-text-utility-serif-600;
    margin-bottom: $cdr-space-three-quarter-x;
    padding: 0;
    quotes: "“" "”" "‘" "’";
    text-indent: -15px;
    @include cdr-md-mq-up {
      @include cdr-text-heading-display-800;
    }

    &::before {
      content: open-quote;
      position: relative;
    }
    &::after {
      content: close-quote;
    }
  }

  &__cta-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: $cdr-space-one-x;
    margin-top: $cdr-space-one-and-a-half-x;
  }
}
</style>
